import { map, find, compose, isEqual, get, filter, first, isEmpty, reject, eq } from 'lodash/fp';
import { createSelector, createStructuredSelector } from 'reselect';
import { createHandler } from '@mews/std';
import { Option } from '@mews-commander/legacy-data-structures';
import { localizeText } from '@mews-commander/utils';
import { formValuesSelector } from '@mews-commander/legacy-form';
import {
    selectionHasBooker,
    selectionIsBeforeCheckIn,
    selectionIsCanceled,
    selectionIsDefinite,
    selectionIsUnCheckInable,
    selectionIsUnCheckOutable,
    selectionIsUnconfirmed,
} from '../reservationModule/selectors/reservationModuleState/reservationSelection';
import MailType from './enums/MailType';
import ResendEmailReceiver from './enums/ResendEmailReceiver';
import { mailTypeTextKeyMap, resendEmailReceiverTextKeyMap } from './enums/textKeyMaps';

const ALL_RECEIVERS = [ResendEmailReceiver.Customer, ResendEmailReceiver.Booker, ResendEmailReceiver.Custom];
const WITHOUT_BOOKER = [ResendEmailReceiver.Customer, ResendEmailReceiver.Custom];
const JUST_BOOKER = [ResendEmailReceiver.Booker];
const JUST_CUSTOMER = [ResendEmailReceiver.Customer];

// @ts-expect-error $TSFixMeError: No overload matches this call.
const getEmailTypeFlags = createStructuredSelector({
    [MailType.Cancellation]: selectionIsCanceled,
    [MailType.Confirmation]: selectionIsDefinite,
    [MailType.BookerConfirmation]: createSelector(
        selectionIsDefinite,
        selectionHasBooker,
        (isDefinite, hasBooker) => isDefinite && hasBooker
    ),
    [MailType.Quotation]: selectionIsUnconfirmed,
    [MailType.BeforeStart]: createSelector(
        selectionIsBeforeCheckIn,
        selectionIsDefinite,
        (isBeforeCheckIn, isDefinite) => isBeforeCheckIn && isDefinite
    ),
    [MailType.ReleaseReminder]: selectionIsUnconfirmed,
    [MailType.AfterStart]: selectionIsUnCheckInable,
    [MailType.AfterEnd]: selectionIsUnCheckOutable,
});

const getReceiversForMailType = createHandler(
    {
        [MailType.AfterStart]: WITHOUT_BOOKER,
        [MailType.AfterEnd]: WITHOUT_BOOKER,
        [MailType.Confirmation]: WITHOUT_BOOKER,
        [MailType.BeforeStart]: JUST_CUSTOMER,
        [MailType.BookerConfirmation]: JUST_BOOKER,
    },
    ALL_RECEIVERS
);

const getFilteredReceiversForMailType = ({ hasBooker }: $TSFixMe) =>
    compose(
        filter((receiver) => hasBooker || receiver !== ResendEmailReceiver.Booker),
        getReceiversForMailType
    );

const getReceiverValue = compose(get('receiver'), formValuesSelector);
const getMailTypeValue = compose(get('mailType'), formValuesSelector);

export const getMailTypeOptions = createSelector(getEmailTypeFlags, selectionHasBooker, (enabledTypesMap, hasBooker) =>
    compose(
        map((enumValue) => {
            // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
            const receivers = getFilteredReceiversForMailType({ hasBooker })(enumValue);
            return Option({
                // @ts-expect-error $TSFixMeError: Type 'unknown' is not assignable to type 'string | number | null | undefined'.
                value: enumValue,
                // @ts-expect-error $TSFixMeError: Type 'unknown' cannot be used as an index type.
                label: localizeText(mailTypeTextKeyMap[enumValue]),
                // @ts-expect-error $TSFixMeError: Type 'unknown' cannot be used as an index type.
                disabled: !enabledTypesMap[enumValue] || isEmpty(receivers),
            });
        }),
        reject(eq(MailType.NotSelected))
    )(MailType)
);

export const getReceiverOptions = createSelector(getMailTypeValue, selectionHasBooker, (mailType, hasBooker) => {
    // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
    const receivers = getFilteredReceiversForMailType({ hasBooker })(mailType);

    return map((enumValue) =>
        Option({
            // @ts-expect-error $TSFixMeError: Type 'unknown' is not assignable to type 'string | number | null | undefined'.
            value: enumValue,
            // @ts-expect-error $TSFixMeError: Type 'unknown' cannot be used as an index type.
            label: localizeText(resendEmailReceiverTextKeyMap[enumValue]),
        })
    )(receivers);
});

export const showCustomEmail = compose(isEqual(ResendEmailReceiver.Custom), getReceiverValue);
const getInitialMailType = compose(get('value'), find({ disabled: false }), getMailTypeOptions);

export const getDefaultReceiverForMailType = compose(first, getReceiversForMailType);

export const getInitialValues = createSelector(getInitialMailType, (mailType) => ({
    // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
    receiver: getDefaultReceiverForMailType(mailType),
    mailType,
}));
